import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  accessRequestApprove,
  api,
  emergencyAcknowledge,
  fmtShortDate,
  overviewTodoAction,
  type GoLiveQueue,
  type OverviewTodo,
  type TodoDone,
  type TodoDoneRow,
  type TodoGroup,
  type TodoRow,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import type { QueryState } from '../../useQuery';
import { ActionButton } from '../../ui/ReadOnly';
import { HonestyBadge } from '../../ui/HonestyBadge';
import { Skeleton } from '../../ui/states';
import { pilotView } from './GoLiveLine';
import './todo.css';
import { BuildLabel } from '../../ui/buildLabels';

/**
 * The Overview To do tab (Console V4, handoff "Overview · To do tab"; design ovTodoSuper,
 * ovTodoOperator and faTodoVals, L37222-37479, markup L5231-5348 and L5953-6005).
 *
 * Every row, its order and its clocks come from GET /overview/todo; the page only prints
 * them. A tap acts through the route that does the thing: Acknowledge, Assign, Mark done
 * and Call through POST /overview/todo/actions (idempotent by the id made here), Approve,
 * Nudge, Take it and Claim through their own routes. Each lands in Done today, which reads
 * the Truth Ledger's audit trail (GET /overview/todo/done), so it survives a reload.
 */

export type TodoVariant = 'super' | 'operator' | 'building';

const MAIN: TodoGroup[] = ['now', 'today'];
const GROUP_LABEL: Record<TodoGroup, string> = { now: 'Now', today: 'Today', week: 'This week', month: 'This month', later: 'Later' };

/** The design's wait clock: "now" under a minute, minutes, hours to two days, then days. */
export function todoDur(m: number): string {
  return m < 1 ? 'now' : m < 60 ? `${Math.round(m)}m` : m < 2880 ? `${Math.round(m / 60)}h` : `${Math.round(m / 1440)}d`;
}
const waited = (r: TodoRow, nowMs: number) => (r.openedAt ? Math.max(0, (nowMs - Date.parse(r.openedAt)) / 60000) : 0);
export const isLate = (r: TodoRow, nowMs: number) => r.limitMinutes !== null && waited(r, nowMs) > r.limitMinutes;

/** The row's second line: who has it, then what gets worse (a clock when there is one). */
export function todoNote(r: TodoRow, nowMs: number): string {
  const who = r.assignedTo ? `Assigned to ${r.assignedTo.name ?? 'a staff member'}` : r.ownerLine;
  const worse = r.worse ?? (r.nextEscalationAt && r.nextEscalationLabel ? `${r.nextEscalationLabel} in ${todoDur(Math.max(0, (Date.parse(r.nextEscalationAt) - nowMs) / 60000))}` : null);
  return [who, worse].filter(Boolean).join(' · ');
}

/** "N things need you now" / "Nothing urgent. N to decide." / "All clear". */
export function todoHead(rows: TodoRow[], variant: TodoVariant): { text: string; hot: boolean } {
  const now = rows.filter((r) => r.group === 'now').length;
  const main = rows.filter((r) => MAIN.includes(r.group)).length;
  if (now) return { text: `${now} ${now === 1 ? 'thing needs you now' : 'things need you now'}`, hot: true };
  if (variant === 'super') return { text: rows.length ? `Nothing urgent. ${rows.length} to decide.` : 'All clear', hot: false };
  if (variant === 'building') return { text: main ? `Nothing urgent. ${main} for today.` : 'All clear for today', hot: false };
  return { text: main ? `Nothing urgent. ${main} for today.` : 'All clear', hot: false };
}

const atLabel = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
/** A v4 uuid for the action (the server refuses anything else). randomUUID needs a secure
 *  context, so outside one the same shape is built from random hex digits. */
export const newId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  const h = (n: number) => Array.from({ length: n }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  return `${h(8)}-${h(4)}-4${h(3)}-${'89ab'[Math.floor(Math.random() * 4)]}${h(3)}-${h(12)}`;
};

/** One tap on a row: the write, then the list and Done today read again. */
function useTodoActions(todo: QueryState<OverviewTodo>, done: QueryState<TodoDone>) {
  const { client } = useSession();
  const navigate = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const after = useCallback(() => {
    todo.reload();
    done.reload();
  }, [todo, done]);
  const record = useCallback(
    (r: TodoRow, action: 'acknowledge' | 'assign' | 'done' | 'call', extra: { label?: string; assignTo?: string } = {}) =>
      overviewTodoAction(client, { body: { id: newId(), rowId: r.id, action, targetId: r.action.targetId, label: extra.label ?? r.title, assignTo: extra.assignTo } }),
    [client],
  );
  const act = useCallback(
    async (r: TodoRow, extra: { assignTo?: string } = {}) => {
      const k = r.action.kind;
      if (k === 'go' || k === 'reply') {
        if (r.action.to) navigate(r.action.to);
        return;
      }
      setBusy(r.id);
      setError(null);
      try {
        if (k === 'acknowledge') await record(r, 'acknowledge');
        else if (k === 'approve') await accessRequestApprove(client, { params: { id: r.action.targetId! } });
        else if (k === 'nudge') await api.goLiveNudge(client, { params: { facilityId: r.action.targetId! }, body: {} });
        else if (k === 'take') await api.goLiveTake(client, { params: { facilityId: r.action.targetId! }, body: {} });
        else if (k === 'claim') await emergencyAcknowledge(client, { body: { incidentId: r.action.targetId! } });
        else if (k === 'call') await record(r, 'call');
        else if (k === 'assign' && extra.assignTo) await record(r, 'assign', { assignTo: extra.assignTo });
        else if (k === 'done') await record(r, 'done', { label: r.assignedTo ? `${r.title}, done by ${r.assignedTo.name ?? 'staff'}` : `${r.title}, done` });
        after();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'That did not go through.');
      } finally {
        setBusy(null);
      }
    },
    [client, navigate, record, after],
  );
  return { act, busy, error };
}

/** The value, tag and wording one row prints. */
function rowView(r: TodoRow, nowMs: number, variant: TodoVariant) {
  const w = waited(r, nowMs);
  const late = isLate(r, nowMs);
  const tone = late ? 'late' : r.group === 'now' ? 'now' : r.group === 'today' ? 'today' : 'later';
  const hasClock = !!r.openedAt;
  return {
    v: hasClock ? todoDur(w) : variant === 'super' ? 'new' : '',
    vClass: `td-row__v${late ? ' td-row__v--late' : hasClock ? '' : ' td-row__v--new'}`,
    tag: late ? `Late · ${GROUP_LABEL[r.group]}` : GROUP_LABEL[r.group],
    tagClass: `td-tag td-tag--${tone}`,
    note: todoNote(r, nowMs),
  };
}

function SoonLine({ q, nowMs, building }: { q: OverviewTodo; nowMs: number; building?: boolean }) {
  if (!q.soon.length) return null;
  return (
    <div className={`td-soon${building ? ' td-soon--fa' : ''}`}>
      <span className="td-caps">Coming up</span>
      {q.soon.map((s, i) => {
        const when = s.style === 'label' ? s.label ?? '' : s.style === 'date' ? fmtShortDate(s.at) : `in ${todoDur(Math.max(0, (Date.parse(s.at ?? '') - nowMs) / 60000))}`;
        const body = (
          <>
            <b>{when}</b>
            <span>{s.what}</span>
          </>
        );
        return building || !s.to ? (
          <span key={i} className="td-soon__item">
            {body}
          </span>
        ) : (
          <Link key={i} to={s.to} className="td-soon__item td-soon__item--link">
            {body}
          </Link>
        );
      })}
    </div>
  );
}

function DoneRows({ rows, empty, inline }: { rows: TodoDoneRow[]; empty: string; inline?: boolean }) {
  if (inline) {
    return (
      <div className="td-doneline">
        <span className="td-caps">Done today</span>
        {rows.length === 0 ? (
          <span className="td-doneline__none">{empty}</span>
        ) : (
          rows.map((d) => (
            <span key={d.id} className="td-doneline__item">
              <span className="td-done__at">{atLabel(d.at)}</span>
              <span className="td-doneline__what">{d.what}</span>
              <span className="td-done__by">{d.by}</span>
            </span>
          ))
        )}
      </div>
    );
  }
  return rows.length === 0 ? (
    <div className="td-done__none">{empty}</div>
  ) : (
    <>
      {rows.map((d) => (
        <div key={d.id} className="td-done__row">
          <span className="td-done__at">{atLabel(d.at)}</span>
          <span className="td-done__what">{d.what}</span>
          <span className="td-done__by">{d.by}</span>
        </div>
      ))}
    </>
  );
}

function Unavailable({ q }: { q: OverviewTodo }) {
  if (!q.unavailable.length) return null;
  return <div className="td-unavail">{`Not on this list because the source did not answer: ${q.unavailable.join(', ')}. Those items are missing, not clear.`}</div>;
}

// ── Super Admin and Operator ──────────────────────────────────────────────────

export interface FleetGridData {
  facilities: { id: string; name: string; full: string; score: string; delta: string | null; tone: 'good' | 'act' | 'none' }[] | null;
  money: { id: string; name: string; amount: string }[] | null;
  moneyNote: ReactNode;
  incidents: { id: string; what: string; note: string; age: string }[] | null;
}

export function FleetTodo({
  variant,
  todo,
  done,
  golive,
  greeting,
  tenantLabel,
  clock,
  lede,
  grid,
  onOpenQueue,
}: {
  variant: 'super' | 'operator';
  todo: QueryState<OverviewTodo>;
  done: QueryState<TodoDone>;
  golive: QueryState<GoLiveQueue>;
  greeting: string;
  tenantLabel: string;
  clock: string;
  /** The Operator's right-hand figure (Atlas conversations); the Super Admin's is the list's size. */
  lede: { v: string; k: string } | null;
  grid: FleetGridData;
  onOpenQueue: () => void;
}) {
  const { act, busy, error } = useTodoActions(todo, done);
  const nowMs = Date.now();
  const q = todo.data;
  const rows = q?.rows ?? [];
  const main = rows.filter((r) => MAIN.includes(r.group));
  const rest = rows.filter((r) => !MAIN.includes(r.group));
  const head = todoHead(rows, variant);
  const doneRows = done.data?.rows ?? [];
  const right = variant === 'super' ? { v: String(rows.length), k: rows.length === 1 ? 'decision on your list' : 'decisions on your list' } : lede;

  return (
    <div className="td" data-screen-label="Overview · To do" data-testid="overview-todo">
      <div className="td-top">
        <div className="td-top__left">
          <div className="td-top__meta">
            {greeting}{' · '}{tenantLabel}{' · '}<span className="tnum">{clock}</span>
          </div>
          <div className={`td-head${head.hot ? ' td-head--hot' : ''}`}>{q ? head.text : todo.error ? 'Could not load the list' : 'Loading…'}</div>
        </div>
        {right && (
          <div className="td-top__right">
            <div className="td-top__v">{right.v}</div>
            <div className="td-top__k">{right.k}</div>
          </div>
        )}
      </div>

      <div className="td-list">
        {!q && todo.loading && <Skeleton rows={4} />}
        {todo.error && !q && <div className="td-unavail">{`The To do list could not load (request ${todo.error.requestId ?? 'no id'}).`}</div>}
        {main.map((r) => {
          const v = rowView(r, nowMs, variant);
          return (
            <div key={r.id} className="td-row">
              <span className={v.vClass}>{v.v}</span>
              <span className="td-row__mid">
                <span className="td-row__line">
                  <span className="td-row__k">{r.title}</span>
                  <span className={v.tagClass}>{v.tag}</span>
                  {r.basis === 'modeled' && <HonestyBadge flags={[{ basis: 'modeled' }]} figure={r.title} className="ov-badge-mini" hide={['MODELED']} />}
                </span>
                <span className="td-row__note">{v.note}</span>
              </span>
              <ActionButton className="td-btn" disabled={busy === r.id} onClick={() => act(r)}>
                {r.action.label}
              </ActionButton>
            </div>
          );
        })}
        {q && rows.length === 0 && <div className="td-clear">All clear. Nothing needs a decision from you.</div>}
        {q && <SoonLine q={q} nowMs={nowMs} />}
        {rest.length > 0 && (
          <div className="td-rest">
            <span className="td-rest__k">Also on the list</span>
            {rest.map((r) => {
              const label = variant === 'super' ? `${r.title} (${GROUP_LABEL[r.group].toLowerCase()})` : r.title;
              return r.action.to ? (
                <Link key={r.id} to={r.action.to} title={r.ownerLine} className="td-rest__a">
                  {label}
                </Link>
              ) : (
                <span key={r.id} title={r.ownerLine} className="td-rest__a">
                  {label}
                </span>
              );
            })}
          </div>
        )}
      </div>
      {error && <div className="td-unavail" role="alert">{error}</div>}
      {q && <Unavailable q={q} />}

      {variant === 'operator' ? (
        <>
          <div className="td-grid">
            <div className="td-col">
              <div className="td-colhead">
                <span className="td-colhead__t">Facilities</span>
                <span className="td-colhead__m">worst first</span>
                <span className="td-sp" />
                <Link to="/performance" className="td-colhead__a">
                  All
                </Link>
              </div>
              {(grid.facilities ?? []).map((f) => (
                <Link key={f.id} to="/performance" title={f.full} className="td-gridrow">
                  <span className="td-gridrow__name">{f.name}</span>
                  <span className="td-gridrow__v">{f.score}</span>
                  <span className={`td-gridrow__d td-gridrow__d--${f.tone}`}>{f.delta ?? ''}</span>
                </Link>
              ))}
            </div>
            <div className="td-col">
              <div className="td-colhead">
                <span className="td-colhead__t">Money on the table</span>
                <BuildLabel><span className="td-chip">MODELED</span></BuildLabel>
              </div>
              {(grid.money ?? []).map((m) => (
                <Link key={m.id} to="/capture" className="td-gridrow">
                  <span className="td-gridrow__name">{m.name}</span>
                  <span className="td-gridrow__v">{m.amount}</span>
                </Link>
              ))}
              <div className="td-moneynote">{grid.moneyNote}</div>
            </div>
            <div className="td-col">
              <div className="td-colhead">
                <span className="td-colhead__t">Open incidents</span>
                <span className="td-sp" />
                <Link to="/oversight" className="td-colhead__a">
                  Oversight
                </Link>
              </div>
              {grid.incidents && grid.incidents.length === 0 && <div className="td-noinc">None open</div>}
              {(grid.incidents ?? []).map((i) => (
                <div key={i.id} className="td-gridrow td-gridrow--inc">
                  <span className="td-inc__text">
                    <span className="td-inc__what">{i.what}</span>
                    <span className="td-inc__note">{i.note}</span>
                  </span>
                  <span className="td-inc__age">{i.age}</span>
                </div>
              ))}
            </div>
          </div>
          <DoneRows inline rows={doneRows} empty="Nothing cleared yet. Acknowledgements from this list show here with who and when." />
        </>
      ) : (
        <div className="td-panels">
          <div className="td-col">
            <div className="td-colhead">
              <span className="td-colhead__t">Pilots and go-lives</span>
              <span className="td-colhead__m">FOUNDING50 · 90 days</span>
              <span className="td-sp" />
              <button type="button" className="td-colhead__a td-linkbtn" onClick={onOpenQueue}>
                Open queue
              </button>
            </div>
            {golive.loading && !golive.data && <Skeleton rows={3} />}
            {(golive.data?.rows ?? []).map((g) => {
              const p = pilotView(g, null);
              const short = g.name.replace(' Memorial Home', '').replace(' Health & Rehab', '');
              const next = g.state === 'pilot' ? p.proj : `${p.proj.replace('On pace for', 'Live')}${g.projectedGoLive ? ', MODELED' : ''}`;
              return (
                <Link key={g.facilityId} to={g.state === 'pilot' ? '/performance' : '/overview'} title={g.name} className="td-pilot" onClick={g.state === 'pilot' ? undefined : (e) => { e.preventDefault(); onOpenQueue(); }}>
                  <span className="td-pilot__name">{short}</span>
                  <span className={`golive__state golive--${p.tone}`}>{p.state}</span>
                  <span className="td-pilot__prog">
                    <span className="td-pilot__progn">{`${g.progress.done} of ${g.progress.total}`}</span>
                    <span className="td-pilot__bar">
                      <span className={`golive__fill golive__fill--${p.fill}`} style={{ width: `${p.pct}%` }} />
                    </span>
                  </span>
                  <span className="td-pilot__next">{next}</span>
                </Link>
              );
            })}
          </div>
          <div className="td-col">
            <div className="td-colhead">
              <span className="td-colhead__t">Done today</span>
              <span className="td-colhead__m">what you cleared here</span>
            </div>
            <DoneRows rows={doneRows} empty="Nothing cleared yet. Approvals, nudges and take-overs from this list show here with who and when." />
          </div>
        </div>
      )}
      <div className="td-foot">
        <span>The full picture stays on Overview. Speeds and objectives are in Oversight.</span>
        <Link to="/oversight" className="td-foot__a">
          Open Oversight
        </Link>
      </div>
    </div>
  );
}

// ── Facility Admin ────────────────────────────────────────────────────────────

export function BuildingTodo({ todo, done, tenantLabel }: { todo: QueryState<OverviewTodo>; done: QueryState<TodoDone>; tenantLabel: string }) {
  const { act, busy, error } = useTodoActions(todo, done);
  const [picking, setPicking] = useState<string | null>(null);
  const nowMs = Date.now();
  const q = todo.data;
  const rows = useMemo(() => q?.rows ?? [], [q]);
  const main = rows.filter((r) => MAIN.includes(r.group));
  const rest = rows.filter((r) => !MAIN.includes(r.group));
  const head = todoHead(main, 'building');
  const staff = q?.staff ?? [];

  return (
    <div className="td td--fa" data-testid="overview-todo-building">
      <div className="td-top td-top--fa">
        <span className="td-top__left td-top__left--fa">
          <span className="td-top__meta td-top__meta--fa">{tenantLabel}{' · your building only'}</span>
          <span className={`td-head${head.hot ? ' td-head--hot' : ''}`}>{q ? head.text : todo.error ? 'Could not load the list' : 'Loading…'}</span>
        </span>
      </div>
      <div className="td-list td-list--fa">
        {!q && todo.loading && <Skeleton rows={4} />}
        {todo.error && !q && <div className="td-unavail">{`The To do list could not load (request ${todo.error.requestId ?? 'no id'}).`}</div>}
        {q && main.length === 0 && <div className="td-clear">All clear. Nothing on your list.</div>}
        {main.map((r) => {
          const v = rowView(r, nowMs, 'building');
          const assign = r.action.kind === 'assign';
          return (
            <div key={r.id} className="td-farow">
              <div className="td-farow__main">
                <span className={v.vClass}>{v.v}</span>
                <span className="td-farow__mid">
                  <span className="td-row__line">
                    <span className="td-farow__k">{r.title}</span>
                    <span className={v.tagClass}>{v.tag}</span>
                  </span>
                  <span className="td-farow__note">{v.note}</span>
                </span>
                <ActionButton className="td-btn td-btn--fa" disabled={busy === r.id} onClick={() => (assign ? setPicking((p) => (p === r.id ? null : r.id)) : act(r))}>
                  {r.action.label}
                </ActionButton>
              </div>
              {assign && picking === r.id && (
                <div className="td-pick">
                  <span className="td-pick__k">Assign to</span>
                  {staff.length === 0 && <span className="td-pick__none">Nobody is on shift to assign.</span>}
                  {staff.filter((s) => s.userId).map((s) => (
                    <ActionButton
                      key={s.userId}
                      className="td-pick__btn"
                      onClick={() => {
                        setPicking(null);
                        void act(r, { assignTo: s.userId! });
                      }}
                    >
                      {`${s.name} · ${s.residents ?? '–'}`}
                    </ActionButton>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {error && <div className="td-unavail" role="alert">{error}</div>}
      {q && <SoonLine q={q} nowMs={nowMs} building />}
      {rest.length > 0 && (
        <div className="td-rest td-rest--fa">
          <span className="td-rest__k">Also on the list</span>
          {rest.map((r) => (
            <Link key={r.id} to={r.action.to ?? '/overview'} className="td-rest__a td-rest__a--fa">
              {r.title}
            </Link>
          ))}
        </div>
      )}
      {q && <Unavailable q={q} />}
      <div className="td-panels td-panels--fa">
        <div className="td-col">
          <div className="td-colhead">
            <span className="td-colhead__t">Who has what</span>
            <span className="td-colhead__m">residents per person this shift</span>
          </div>
          {q && staff.length === 0 && q.unassigned === null && <div className="td-done__none">Nobody is on shift right now, so there is nothing to split.</div>}
          {staff.map((s) => (
            <div key={s.userId ?? s.name} className="td-staff">
              <span className="td-staff__name">{s.name}</span>
              <span className="td-staff__role">{s.role}</span>
              <span className="td-staff__n">{s.residents === null ? '–' : s.residents}</span>
            </div>
          ))}
          {q && q.unassigned !== null && (
            <div className="td-staff">
              <span className="td-staff__name">Unassigned</span>
              <span className="td-staff__role">nobody has these</span>
              <span className={`td-staff__n${q.unassigned ? ' td-staff__n--act' : ''}`}>{q.unassigned}</span>
            </div>
          )}
        </div>
        <div className="td-col">
          <div className="td-colhead">
            <span className="td-colhead__t">Done today</span>
            <span className="td-colhead__m">saved to the Truth Ledger</span>
          </div>
          <DoneRows rows={done.data?.rows ?? []} empty="Nothing cleared yet. Claims, assignments and replies from this list show here with who and when." />
        </div>
      </div>
    </div>
  );
}

/** The To do tab's count: the rows on the main list (Now and Today). */
export function todoCount(q: OverviewTodo | null): number | null {
  if (!q) return null;
  const n = q.rows.filter((r) => MAIN.includes(r.group)).length;
  return n || null;
}
